import { useState, useCallback, useEffect, useMemo } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { useLocale } from '../../context/LocaleContext';

/**
 * First allowed group on the slot that lists this variant (slot order wins over global maps).
 * @param {unknown[]} allowedComponentGroups
 * @param {Map<string, { productVariantIds?: string[] }>} componentGroups
 * @param {string} variantId
 * @returns {string | null}
 */
function findFirstAllowedGroupContainingVariant(allowedComponentGroups, componentGroups, variantId) {
  if (!Array.isArray(allowedComponentGroups) || !variantId) return null;
  for (const gid of allowedComponentGroups) {
    const groupIdString = typeof gid === 'string' ? gid : gid.id || gid;
    const g = componentGroups?.get(groupIdString);
    if (g?.productVariantIds?.includes(variantId)) {
      return groupIdString;
    }
  }
  return null;
}

/**
 * Custom hook for KeyboardInput logic and state.
 * @param {Object} options
 * @param {Object} options.group - The group object (for price, etc)
 * @param {string} options.slotId - The slot ID for updating selections
 * @param {number} options.maxLength - Maximum allowed characters
 * @returns {Object} View model for the KeyboardInput component
 */
export function useViewModel({ group, slotId, maxLength }) {
  const { selections, updateSelections, variants, personalisationSlots, componentGroups } = usePersonalisationContext();
  const { formatPrice } = useLocale();
  // Get the values from the slots
  const currentSlot = selections.slots.find(({ id }) => id === slotId);
  const [inputValue, setInputValue] = useState(currentSlot?.selected || '');
  /** @type {[{label: string, productVariantId: string, asciiSymbol: string}[], Function]} */
  const [localSelections, setLocalSelections] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [updatedSlotIds, setUpdatedSlotIds] = useState(new Set());
  const [isUserTyping, setIsUserTyping] = useState(false);

  const groupProductVariants = group.productVariantIds.map(id => variants.get(id)).filter(Boolean);

  const LETTERS = groupProductVariants.map(({ title, id, asciiSymbol }) => ({ label: asciiSymbol || title, productVariantId: id, asciiSymbol }));

  // Update max length based on the number of slots filled.
  const [slotsFilled, setSlotsFilled] = useState(0);

  useEffect(() => {
    const slotsFilled = selections.slots.filter(({ selected, slotMode }) => selected !== null && slotMode === 'SEQUENTIAL').length;

    setSlotsFilled(slotsFilled);
  }, [selections.slots, maxLength]);

  // Keep inputValue in sync with current slot selection
  useEffect(() => {
    // Only sync if we don't have local selections, we're not applying, this slot wasn't just updated, and user isn't typing
    if (localSelections.length === 0 && !isApplying && !updatedSlotIds.has(currentSlot?.id) && !isUserTyping) {
      if (currentSlot?.selected) {
        // If the slot has a selection, try to convert it back to letters
        const selectedVariant = variants.get(currentSlot.selected);
        if (selectedVariant?.asciiSymbol) {
          setInputValue(selectedVariant.asciiSymbol);
        } else {
          setInputValue('');
        }
      } else {
        setInputValue('');
      }
    }
  }, [currentSlot?.selected, currentSlot?.id, variants, localSelections.length, isApplying, updatedSlotIds, isUserTyping]);

  // Reset typing flag when slot changes
  useEffect(() => {
    setIsUserTyping(false);
  }, [slotId]);

  // Clear updated slot IDs when slot changes
  useEffect(() => {
    setUpdatedSlotIds(new Set());
  }, [slotId]);

  // Bulk input change
  const handleInputChange = useCallback((e) => {
    // Mark that user is typing to prevent sync from overriding
    setIsUserTyping(true);

    // @TODO We could do with a better way to ensure we always match the letters to the product variants.
    // Sanitize input to only allow letters a-z (case-insensitive, converted to uppercase)
    let value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
    if (maxLength) value = value.slice(0, maxLength);
    setInputValue(value);

    // Split the input text into an array of letters
    const splitValue = value.split('');
    // Find the product variant ID for each letter
    const dataValues = splitValue.map((inputLetter) => {
      const match = LETTERS.find(({ label }) => label === inputLetter);
      return match;
    }).filter(Boolean);

    // Set the local selections to the product variant IDs (include asciiSymbol for matching)
    setLocalSelections(dataValues.map(({ label, productVariantId, asciiSymbol }) => ({ label, productVariantId, asciiSymbol })));

    // Clear updated slot IDs when input changes
    setUpdatedSlotIds(new Set());
  }, [maxLength, LETTERS]);

  // Apply button: update context
  const handleApply = useCallback(() => {
    // Set applying flag to prevent input syncing
    setIsApplying(true);

    // Get all sequential slots in order
    const sequentialSlots = selections.slots.filter(({ slotMode }) => slotMode === 'SEQUENTIAL');

    // Check if we're editing an existing slot (current slot has a selection)
    const isEditingExistingSlot = currentSlot?.selected !== null;

    if (isEditingExistingSlot && localSelections.length === 1 && currentSlot) {
      // Editing existing slot: replace the current slot's value
      const { productVariantId } = localSelections[0];
      const fullSlotData = personalisationSlots.get(currentSlot.id);
      const componentGroupId = findFirstAllowedGroupContainingVariant(
        fullSlotData?.allowedComponentGroups,
        componentGroups,
        productVariantId
      );

      updateSelections({
        slotId: currentSlot.id,
        itemSelected: productVariantId,
        ...(componentGroupId && { componentGroupId })
      });
      // Track which slots we've updated
      setUpdatedSlotIds((prev) => new Set([...prev, currentSlot.id]));
    } else {
      // Adding new characters: find empty slots and fill them
      const emptySlots = sequentialSlots.filter(({ selected }) => selected === null);

      // Apply selections only to empty slots
      // For each letter, find the matching variant in the target slot's allowed component groups
      localSelections.forEach(({ label, asciiSymbol }, index) => {
        const targetSlot = emptySlots[index];
        if (!targetSlot || !targetSlot.id) return;

        // Get the full slot data to access allowedComponentGroups
        const fullSlotData = personalisationSlots.get(targetSlot.id);
        if (!fullSlotData) {
          console.warn('KeyboardInput: Could not find full slot data for', targetSlot.id);
          return;
        }

        // Get all variants from the target slot's allowed component groups
        const targetSlotAllowedGroups = fullSlotData.allowedComponentGroups || [];
        const targetSlotVariants = targetSlotAllowedGroups
          .flatMap((groupId) => {
            // Handle both string IDs and component group objects
            const groupIdString = typeof groupId === 'string' ? groupId : groupId.id || groupId;
            const componentGroup = componentGroups?.get(groupIdString);
            if (!componentGroup) return [];
            return (componentGroup.productVariantIds || []).map((id) => variants.get(id)).filter(Boolean);
          })
          .filter(Boolean);

        // Find the matching variant by asciiSymbol in the target slot's allowed groups
        const matchingVariant = targetSlotVariants.find(
          (variant) => variant.asciiSymbol === asciiSymbol || variant.asciiSymbol === label
        );

        if (!matchingVariant) {
          console.warn('KeyboardInput: Could not find matching variant for', { label, asciiSymbol, slotId: targetSlot.id });
          return;
        }

        const componentGroupId = findFirstAllowedGroupContainingVariant(
          fullSlotData.allowedComponentGroups,
          componentGroups,
          matchingVariant.id
        );

        updateSelections({
          slotId: targetSlot.id,
          itemSelected: matchingVariant.id,
          ...(componentGroupId && { componentGroupId })
        });
        // Track which slots we've updated
        setUpdatedSlotIds((prev) => new Set([...prev, targetSlot.id]));
      });
    }

    // Clear the input and local selections after applying
    setInputValue('');
    setLocalSelections([]);
    setIsUserTyping(false);

    // Reset applying flag after a small delay to allow selections to update
    setTimeout(() => {
      setIsApplying(false);
    }, 100);
  }, [localSelections, selections.slots, updateSelections, currentSlot, componentGroups, personalisationSlots, variants]);

  const handleInputKeyDown = useCallback((event) => {
    if (event.key !== 'Enter') return;
    if (event.nativeEvent?.isComposing || event.isComposing) return;

    event.preventDefault();
    handleApply();
  }, [handleApply]);

  const { priceLabel } = useMemo(() => {

    let prices = groupProductVariants.map(({ price }) => parseFloat(price.amount));
    const theSlot = personalisationSlots.get(slotId);

    if (typeof theSlot?.overridePrice === 'number' && !theSlot.isFreeSlot) {
      prices = [theSlot.overridePrice];
    }

    // Check if all prices are zero, if so return null
    const allPricesZero = prices.every(price => price === 0);
    if (allPricesZero) {
      return {
        minPrice: null,
        maxPrice: null,
        priceLabel: null
      };
    }

    // Get the min and max price from the product variants
    const minPrice = formatPrice({ amount: Math.min(...prices) });
    const maxPrice = formatPrice({ amount: Math.max(...prices) });
    const priceLabel = minPrice === maxPrice ? `${minPrice} Each` : `${minPrice} - ${maxPrice} Each`;

    return {
      minPrice,
      maxPrice,
      priceLabel
    };

  }, [groupProductVariants, personalisationSlots, slotId, formatPrice]);

  // Calculate effective max length - when editing existing slot, allow at least 1 character
  const effectiveMaxLength = useMemo(() => {
    const remainingSlots = maxLength - slotsFilled;
    // If we're editing an existing slot (has a selection), allow at least 1 character
    const hasCurrentSelection = currentSlot?.selected !== null;
    return hasCurrentSelection ? Math.max(1, remainingSlots) : remainingSlots;
  }, [maxLength, slotsFilled, currentSlot?.selected]);

  return {
    inputValue,
    setInputValue,
    handleInputChange,
    handleInputKeyDown,
    handleApply,
    maxLength: effectiveMaxLength,
    priceLabel
  };
}
